<template>
  <article
    class="doc"
    :class="{ 'doc--open': open }"
    role="button"
    tabindex="0"
    :aria-label="`Open order ${label}`"
    data-test="dash-card"
    @click="onCardClick"
    @keydown.enter.prevent="emit('open')"
    @keydown.space.prevent="emit('open')"
  >
    <!--
      AB#22137 — one dashboard row as a card, for a phone. The table's columns,
      read top to bottom: the order and its status, who it is for, what it is,
      and the dates that matter. Tapping anywhere on the card opens the order, as
      clicking a row does; the ⋮ (the row's own actions) sits in the corner.
    -->
    <header class="doc-top">
      <span class="doc-no" data-test="dash-card-no">{{ label }}</span>
      <span v-if="typeSymbol" class="doc-type" :title="typeTitle">{{ typeSymbol }}</span>
      <span class="doc-status" :data-tone="tone" data-test="dash-card-status">
        <i class="doc-dot" :style="{ backgroundColor: TONE_DOT[tone] }" aria-hidden="true"></i>
        {{ statusLabel }}
      </span>
      <span class="doc-menu" @click.stop>
        <slot name="actions" />
      </span>
    </header>

    <h3 class="doc-who" data-test="dash-card-who">{{ who }}</h3>
    <p v-if="sub" class="doc-sub">{{ sub }}</p>

    <p class="doc-facts" data-test="dash-card-facts">
      <span v-if="row.items_total_quantity">{{ row.items_total_quantity }} pcs</span>
      <span v-if="showFactory && row.factory_name">{{ row.factory_name }}</span>
      <span v-if="shipDate">Ships {{ shipDate }}</span>
      <span v-if="row.in_hand_date" :class="inHandClass" data-test="dash-card-in-hand"
        >In hand {{ formatShortDate(row.in_hand_date) }}</span
      >
      <span v-if="row.unresolved_comments_count" class="doc-comments">
        {{ row.unresolved_comments_count }} open comment{{
          row.unresolved_comments_count === 1 ? '' : 's'
        }}
      </span>
    </p>

    <footer class="doc-foot">
      <span class="doc-cta">View order &amp; actions</span>
      <ArrowRightIcon class="doc-cta-ic" aria-hidden="true" />
    </footer>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ArrowRightIcon } from '@heroicons/vue/24/outline';
import { CustimooOrderFlowStatuses, cleanName, formatShortDate } from '@/composables';
import type { DashboardItem } from '@/models';
import { statusTone, TONE_DOT } from '@/utils/orderStatusTone';
import { inHandTone } from '@/utils/inHandDate';

const props = withDefaults(
  defineProps<{
    row: DashboardItem;
    /** The dashboard tab: "Customer rejected" reads only on My orders. */
    currentTab?: string | null;
    showMerchant?: boolean;
    showFactory?: boolean;
    /** The order is open in the pane. */
    open?: boolean;
  }>(),
  { currentTab: null, showMerchant: true, showFactory: true, open: false }
);

const emit = defineEmits<{ (e: 'open'): void }>();

const onCardClick = (event: MouseEvent): void => {
  const target = event.target as HTMLElement | null;
  if (target?.closest?.('button, a, input, select, label, [role="menu"]')) return;
  emit('open');
};

const label = computed(() => {
  const manual = Boolean((props.row.order as any)?.additional_fields?.is_manual_order);
  const no = props.row.item_no || props.row.order?.order_no || '';
  return `${manual ? 'M_' : ''}#${no}`;
});

/** Remakes, reorders and samples carry their symbol; a new order does not. */
const typeSymbol = computed(() => {
  const s = props.row.order_type_symbol ?? props.row.order?.order_type_symbol ?? '';
  return s && s !== 'N' ? s : '';
});
const typeTitle = computed(() =>
  typeSymbol.value === 'R' || typeSymbol.value === 'Ri'
    ? 'Remake'
    : typeSymbol.value === 'RO' || typeSymbol.value === 'RM'
      ? 'Reorder'
      : typeSymbol.value === 'S'
        ? 'Sample'
        : 'Order type'
);

const rejected = computed(() => {
  if (props.currentTab !== 'my_orders') return false;
  if (props.row.status !== 'submitted_for_customer_review') return false;
  let fps: any = props.row.factory_products;
  if (typeof fps === 'string') {
    try {
      fps = JSON.parse(fps);
    } catch {
      fps = [];
    }
  }
  return Array.isArray(fps) && fps.some((fp: any) => fp?.status === 'customer_rejected');
});

const tone = computed(() => (rejected.value ? 'bad' : statusTone(props.row.status)));
const statusLabel = computed(() =>
  rejected.value
    ? (CustimooOrderFlowStatuses.customer_rejected ?? 'Customer Rejected')
    : (CustimooOrderFlowStatuses[props.row.status] ??
      (props.row.status === 'on_hold' ? 'On Hold' : props.row.status))
);

const customer = computed(() =>
  cleanName(props.row.order?.customer_name) ? props.row.order.customer_name : ''
);
const merchant = computed(() => {
  const company = props.row.order?.company_name ?? '';
  return props.row.billing_company_name && props.row.billing_company_name !== company
    ? `${company} / ${props.row.billing_company_name}`
    : company;
});

/** The headline is the merchant for staff who see merchants, else the customer. */
const who = computed(() =>
  props.showMerchant && merchant.value ? merchant.value : customer.value || '—'
);
const sub = computed(() => {
  const parts: string[] = [];
  if (props.showMerchant && merchant.value && customer.value) parts.push(customer.value);
  const ref = props.row.order?.customer_reference_no;
  if (ref) parts.push(ref);
  return parts.join(' · ');
});

const shipDate = computed(() => {
  const v = props.row.est_shipping_date;
  if (!v) return '';
  const d = Array.isArray(v) ? v[v.length - 1] : v;
  return d ? formatShortDate(d) : '';
});

const inHandClass = computed(() => {
  const t = inHandTone(props.row.in_hand_date ?? null, props.row.est_shipping_date);
  return t === 'late' ? 'doc-late' : t === 'tight' ? 'doc-tight' : '';
});
</script>

<style lang="scss">
/* AB#22137 — phone only: the card is rendered by DataTable's card mode. */
.doc {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 14px 12px 16px;
  border-radius: 14px;
  background: var(--surface);
  border: 1px solid var(--border-soft, var(--border));
  color: var(--ink);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.12s ease;
}
.doc:active {
  background: var(--surface-2);
}
.doc:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.doc--open {
  border-color: var(--accent);
  box-shadow: 0 0 0 1px var(--accent);
}
.doc-top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
}
.doc-no {
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}
.doc-type {
  font-size: 11px;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 6px;
  color: var(--danger);
  background: var(--surface-2);
}
.doc-status {
  margin-left: auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 55%;
  padding: 3px 9px;
  border-radius: 999px;
  background: var(--surface-2);
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.doc-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  flex: 0 0 7px;
}
.doc-menu {
  display: inline-flex;
  margin-right: -6px;
}
.doc-who {
  margin: 2px 0 0;
  font-size: 15.5px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.doc-sub {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--ink-soft);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.doc-facts {
  margin: 2px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 13px;
  color: var(--ink-soft);
  font-variant-numeric: tabular-nums;
}
.doc-late {
  color: var(--danger);
  font-weight: 700;
}
.doc-tight {
  color: var(--warning);
  font-weight: 700;
}
.doc-comments {
  color: var(--warning);
  font-weight: 600;
}
.doc-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid var(--border-soft, var(--border));
  color: var(--accent);
}
.doc-cta {
  font-size: 13.5px;
  font-weight: 700;
}
.doc-cta-ic {
  width: 18px;
  height: 18px;
}
</style>
